import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { ScrapeJob } from '../../types';
import './AdminDashboard.css';

interface AdminUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  created_at: string;
  last_login: string | null;
  is_approved: boolean;
  is_blocked: boolean;
  is_inactive?: boolean;
  blocked_reason: string | null;
  has_race_goal: boolean;
  race_name: string | null;
  total_workouts: number;
  coach_messages_this_week: number;
}

interface PendingUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  created_at: string;
  strava_profile: string | null;
}

interface AdminStats {
  total_users: number;
  new_users_this_week: number;
  blocked_users: number;
  coach_messages_this_week: number;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [adminEmail, setAdminEmail] = useState('');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [config, setConfig] = useState<Record<string, { value: string; description: string }>>({});
  const [scrapeJobs, setScrapeJobs] = useState<ScrapeJob[]>([]);
  const [scrapeStats, setScrapeStats] = useState<any | null>(null);
  const [triggeringSource, setTriggeringSource] = useState<string | null>(null);
  const [togglingSource, setTogglingSource] = useState<string | null>(null);

  // Curated races state
  const [curatedRaces, setCuratedRaces] = useState<any[]>([]);
  const [curatedLoading, setCuratedLoading] = useState(false);
  const [editingCurated, setEditingCurated] = useState<number | null>(null);
  const [editingCuratedData, setEditingCuratedData] = useState<any>({});
  const [showAddCurated, setShowAddCurated] = useState(false);
  const [newCurated, setNewCurated] = useState<any>({
    name: '', city: '', state: '', country: 'US', race_tier: 'major',
    typical_month: 1, terrain: 'road', website_url: '', registration_url: '', description: '',
  });
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'users' | 'scrapers' | 'curated' | 'email'>('pending');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [blockingId, setBlockingId] = useState<number | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [editingConfig, setEditingConfig] = useState<string | null>(null);
  const [configValue, setConfigValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    api.adminCheck()
      .then(res => {
        if (!res.authenticated) { navigate('/admin'); return; }
        setAdminEmail(res.email || '');
        loadData();
      })
      .catch(() => navigate('/admin'));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [navigate]);

  useEffect(() => {
    if (activeTab === 'curated' && curatedRaces.length === 0 && !curatedLoading) {
      loadCurated();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Auto-poll every 5s while any scraper is running
  useEffect(() => {
    const anyRunning = scrapeStats?.sources && Object.values(scrapeStats.sources).some((s: any) => s.is_running);
    if (anyRunning) {
      if (!pollRef.current) {
        pollRef.current = setInterval(async () => {
          const [jobsData, statsData] = await Promise.all([
            api.adminGetScrapeJobs().catch(() => []),
            api.adminGetScrapeStats().catch(() => null),
          ]);
          setScrapeJobs(jobsData);
          setScrapeStats(statsData);
        }, 5000);
      }
    } else {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
  }, [scrapeStats]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, statsData, configData, jobsData, scrapeStatsData, pendingData] = await Promise.all([
        api.adminGetUsers(),
        api.adminGetStats(),
        api.adminGetConfig(),
        api.adminGetScrapeJobs().catch(() => []),
        api.adminGetScrapeStats().catch(() => null),
        api.adminGetPendingUsers().catch(() => []),
      ]);
      setUsers(usersData);
      setStats(statsData);
      setConfig(configData);
      setScrapeJobs(jobsData);
      setScrapeStats(scrapeStatsData);
      setPendingUsers(pendingData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await api.adminLogout();
    sessionStorage.removeItem('admin_authenticated');
    navigate('/admin');
  };

  const handleBlock = async (userId: number) => {
    if (!blockReason.trim()) { setError('Block reason is required'); return; }
    try {
      await api.adminBlockUser(userId, blockReason);
      setBlockingId(null);
      setBlockReason('');
      setSuccessMsg('User blocked.');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSetInactive = async (userId: number, inactive: boolean) => {
    try {
      await api.adminSetInactive(userId, inactive);
      setSuccessMsg(inactive ? 'User marked inactive — no more auto plans or emails.' : 'User reactivated.');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUnblock = async (userId: number) => {
    try {
      await api.adminUnblockUser(userId);
      setSuccessMsg('User unblocked.');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleApprove = async (userId: number) => {
    setApprovingId(userId);
    try {
      await api.adminApproveUser(userId);
      setSuccessMsg('User approved.');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setApprovingId(null);
    }
  };

  const handleDeleteUser = async (userId: number, userName: string) => {
    if (!window.confirm(`Permanently delete ${userName} and all their data? This cannot be undone.`)) return;
    try {
      await api.adminDeleteUser(userId);
      setSuccessMsg(`${userName} deleted.`);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeletePlans = async (userId: number, userName: string) => {
    if (!window.confirm(`Delete ALL training plans for ${userName}? This cannot be undone.`)) return;
    try {
      const res = await api.adminDeleteUserPlans(userId);
      setSuccessMsg(`Deleted ${res.plans_deleted} plan(s) for ${userName}.`);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSaveConfig = async (key: string) => {
    try {
      await api.adminUpdateConfig(key, configValue);
      setEditingConfig(null);
      setSuccessMsg(`Config "${key}" updated.`);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleTriggerScrape = async (source: string) => {
    setTriggeringSource(source);
    try {
      const res = await api.adminTriggerScrape(source);
      setSuccessMsg(res.message);
      setTimeout(loadData, 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setTriggeringSource(null);
    }
  };

  const handleToggleScraper = async (source: string) => {
    setTogglingSource(source);
    try {
      await api.adminToggleScraper(source);
      const statsData = await api.adminGetScrapeStats().catch(() => null);
      setScrapeStats(statsData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setTogglingSource(null);
    }
  };

  const loadCurated = async () => {
    setCuratedLoading(true);
    try {
      const data = await api.adminGetCurated();
      setCuratedRaces(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCuratedLoading(false);
    }
  };

  const handleAddCurated = async () => {
    if (!newCurated.name.trim()) { setError('Race name is required'); return; }
    try {
      await api.adminAddCurated(newCurated);
      setShowAddCurated(false);
      setNewCurated({ name: '', city: '', state: '', country: 'US', race_tier: 'major', typical_month: 1, terrain: 'road', website_url: '', registration_url: '', description: '' });
      setSuccessMsg('Race added.');
      await loadCurated();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSaveCurated = async (id: number) => {
    try {
      await api.adminUpdateCurated(id, editingCuratedData);
      setEditingCurated(null);
      setSuccessMsg('Race updated.');
      await loadCurated();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteCurated = async (id: number, name: string) => {
    if (!window.confirm(`Delete "${name}" from the curated list?`)) return;
    try {
      await api.adminDeleteCurated(id);
      setSuccessMsg(`"${name}" deleted.`);
      await loadCurated();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const fmtDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString();
  };

  const fmtDateTime = (iso: string | null | undefined) => {
    if (!iso) return '—';
    const utc = iso.endsWith('Z') ? iso : iso + 'Z';
    return new Date(utc).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  };

  const fmtDuration = (secs: number | undefined) => {
    if (!secs) return '—';
    if (secs < 60) return `${secs}s`;
    return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Loading admin dashboard…</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-topbar">
        <div className="admin-topbar-left">
          <h1>Admin Dashboard</h1>
          <span className="admin-email">{adminEmail}</span>
        </div>
        <button className="btn-admin-logout" onClick={handleLogout}>Sign Out</button>
      </div>

      <div className="admin-container">
        {error && (
          <div className="admin-alert admin-alert-error" onClick={() => setError(null)}>
            {error} ✕
          </div>
        )}
        {successMsg && (
          <div className="admin-alert admin-alert-success" onClick={() => setSuccessMsg(null)}>
            {successMsg} ✕
          </div>
        )}

        {/* Tabs */}
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pending {pendingUsers.length > 0 && <span className="admin-tab-badge">{pendingUsers.length}</span>}
          </button>
          <button
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button
            className={`admin-tab ${activeTab === 'scrapers' ? 'active' : ''}`}
            onClick={() => setActiveTab('scrapers')}
          >
            Scrapers {scrapeStats ? `· ${scrapeStats.total_races.toLocaleString()} races` : ''}
          </button>
          <button
            className={`admin-tab ${activeTab === 'curated' ? 'active' : ''}`}
            onClick={() => setActiveTab('curated')}
          >
            Curated Races {curatedRaces.length > 0 ? `· ${curatedRaces.length}` : ''}
          </button>
          <button
            className={`admin-tab ${activeTab === 'email' ? 'active' : ''}`}
            onClick={() => setActiveTab('email')}
          >
            Email
          </button>
        </div>

        {/* Stats */}
        {stats && activeTab === 'users' && (
          <div className="admin-stats-row">
            <div className="admin-stat-card">
              <div className="admin-stat-value">{stats.total_users}</div>
              <div className="admin-stat-label">Total Users</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-value">+{stats.new_users_this_week}</div>
              <div className="admin-stat-label">New This Week</div>
            </div>
            <div className="admin-stat-card blocked">
              <div className="admin-stat-value">{stats.blocked_users}</div>
              <div className="admin-stat-label">Blocked</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-value">{stats.coach_messages_this_week}</div>
              <div className="admin-stat-label">AI Messages (7d)</div>
            </div>
          </div>
        )}

        {/* Scrapers tab */}
        {activeTab === 'scrapers' && (
          <div className="admin-section">
            <div className="admin-scraper-header">
              <h2>Race Scrapers</h2>
              {scrapeStats && (
                <span className="admin-total-races">{scrapeStats.total_races.toLocaleString()} total races in DB</span>
              )}
            </div>

            {/* Per-source status cards */}
            <div className="admin-scraper-cards">
              {([
                { key: 'strava_auto_sync', label: 'Strava Auto Sync', stat1: 'Users', stat2: 'Imported', stat3: 'Updated', stat4: 'Linked' },
                { key: 'runsignup',        label: 'RunSignUp',        stat1: 'Found',  stat2: 'New',      stat3: 'Updated', stat4: null },
                { key: 'ultrasignup',      label: 'UltraSignup',      stat1: 'Found',  stat2: 'New',      stat3: 'Updated', stat4: null },
                { key: 'dedup',            label: 'Dedup',            stat1: 'Found',  stat2: 'Merged',   stat3: null,      stat4: null },
              ] as const).map(({ key: src, label, stat1, stat2, stat3, stat4 }) => {
                const s = scrapeStats?.sources?.[src];
                const isRunning = s?.is_running;
                const lastOk = s?.last_status === 'completed';
                const lastFailed = s?.last_status === 'failed';
                const isSync = src === 'strava_auto_sync';
                return (
                  <div key={src} className={`admin-scraper-card ${isRunning ? 'running' : lastFailed ? 'failed' : lastOk ? 'ok' : ''}`}>
                    <div className="scraper-card-top">
                      <div className="scraper-card-name">
                        <span className={`scraper-status-dot ${isRunning ? 'running' : lastOk ? 'ok' : lastFailed ? 'failed' : 'idle'}`} />
                        <code>{label}</code>
                        {isRunning && <span className="scraper-running-label">Running…</span>}
                      </div>
                      {!isSync && (
                        <label className="scraper-toggle" title={s?.enabled ? 'Disable scraper' : 'Enable scraper'}>
                          <input
                            type="checkbox"
                            checked={s?.enabled ?? true}
                            disabled={togglingSource === src}
                            onChange={() => handleToggleScraper(src)}
                          />
                          <span className="scraper-toggle-track" />
                        </label>
                      )}
                    </div>

                    <div className="scraper-card-stats">
                      <div className="scraper-stat">
                        <span className="scraper-stat-label">Last run</span>
                        <span className="scraper-stat-value">{s?.last_run ? fmtDateTime(s.last_run) : '—'}</span>
                      </div>
                      <div className="scraper-stat">
                        <span className="scraper-stat-label">Duration</span>
                        <span className="scraper-stat-value">{fmtDuration(s?.duration_seconds)}</span>
                      </div>
                      <div className="scraper-stat">
                        <span className="scraper-stat-label">{stat1}</span>
                        <span className="scraper-stat-value">{s?.last_races_found ?? '—'}</span>
                      </div>
                      <div className="scraper-stat">
                        <span className="scraper-stat-label">{stat2}</span>
                        <span className="scraper-stat-value scraper-stat-new">{s?.last_races_new ?? '—'}</span>
                      </div>
                    </div>

                    {lastFailed && s?.last_error && (
                      <div className="scraper-error-msg">{s.last_error}</div>
                    )}

                    {!isSync && (
                      <button
                        className="btn-admin-sm btn-save scraper-run-btn"
                        disabled={!!triggeringSource || isRunning || !s?.enabled}
                        onClick={() => handleTriggerScrape(src)}
                      >
                        {triggeringSource === src ? 'Starting…' : isRunning ? 'Running…' : 'Run Now'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Job history table */}
            <h3 style={{ marginTop: '2rem', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>Job History (last 50)</h3>
            <div className="admin-table-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Found</th>
                    <th>New</th>
                    <th>Updated</th>
                    <th>Deduped</th>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {scrapeJobs.length === 0 && (
                    <tr><td colSpan={9} style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>No scrape jobs yet</td></tr>
                  )}
                  {scrapeJobs.map(j => (
                    <tr key={j.id} className={j.status === 'failed' ? 'admin-row-blocked' : ''}>
                      <td><code>{j.source}</code></td>
                      <td>
                        <span className={`admin-status-badge ${j.status === 'completed' ? 'active' : j.status === 'failed' ? 'blocked' : j.status === 'running' ? 'running' : ''}`}>
                          {j.status}
                        </span>
                      </td>
                      <td>{j.races_found ?? '—'}</td>
                      <td>{j.races_new ?? '—'}</td>
                      <td>{j.races_updated ?? '—'}</td>
                      <td>{j.races_deduped ?? '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(j.started_at)}</td>
                      <td>{fmtDuration(j.duration_seconds)}</td>
                      <td style={{ color: '#f87171', fontSize: '0.75rem', maxWidth: '220px', wordBreak: 'break-word' }}>
                        {j.error_message || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Config */}
        {activeTab === 'users' && <div className="admin-section">
          <h2>App Configuration</h2>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(config).map(([key, cfg]) => (
                <tr key={key}>
                  <td><code>{key}</code></td>
                  <td>
                    {editingConfig === key ? (
                      <input
                        className="admin-inline-input"
                        value={configValue}
                        onChange={e => setConfigValue(e.target.value)}
                      />
                    ) : cfg.value}
                  </td>
                  <td className="admin-cfg-desc">{cfg.description}</td>
                  <td>
                    {editingConfig === key ? (
                      <>
                        <button className="btn-admin-sm btn-save" onClick={() => handleSaveConfig(key)}>Save</button>
                        <button className="btn-admin-sm btn-cancel" onClick={() => setEditingConfig(null)}>Cancel</button>
                      </>
                    ) : (
                      <button className="btn-admin-sm btn-edit" onClick={() => { setEditingConfig(key); setConfigValue(cfg.value); }}>Edit</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}

        {/* Curated Races */}
        {activeTab === 'curated' && (
          <div className="admin-section">
            <div className="admin-curated-header">
              <h2>Curated Major Races ({curatedRaces.length})</h2>
              <button className="btn-admin-sm btn-save" onClick={() => setShowAddCurated(v => !v)}>
                {showAddCurated ? 'Cancel' : '+ Add Race'}
              </button>
            </div>

            {/* Tier summary pills */}
            {curatedRaces.length > 0 && (
              <div className="admin-curated-tier-summary">
                {(['world_major', 'gold_label', 'major', 'iconic_ultra'] as const).map(tier => {
                  const count = curatedRaces.filter(r => r.race_tier === tier).length;
                  if (!count) return null;
                  const labels: Record<string, string> = { world_major: 'World Major', gold_label: 'Gold Label', major: 'Major', iconic_ultra: 'Iconic Ultra' };
                  return <span key={tier} className={`curated-tier-pill curated-tier-${tier.replace('_', '-')}`}>{labels[tier]}: {count}</span>;
                })}
              </div>
            )}

            {/* Add Race form */}
            {showAddCurated && (
              <div className="admin-curated-form">
                <h3>Add New Race</h3>
                <div className="admin-curated-form-grid">
                  <label>Name *
                    <input value={newCurated.name} onChange={e => setNewCurated((p: any) => ({ ...p, name: e.target.value }))} placeholder="Boston Marathon" />
                  </label>
                  <label>City
                    <input value={newCurated.city} onChange={e => setNewCurated((p: any) => ({ ...p, city: e.target.value }))} placeholder="Boston" />
                  </label>
                  <label>State
                    <input value={newCurated.state} onChange={e => setNewCurated((p: any) => ({ ...p, state: e.target.value }))} placeholder="MA" />
                  </label>
                  <label>Country
                    <input value={newCurated.country} onChange={e => setNewCurated((p: any) => ({ ...p, country: e.target.value }))} placeholder="US" />
                  </label>
                  <label>Tier
                    <select value={newCurated.race_tier} onChange={e => setNewCurated((p: any) => ({ ...p, race_tier: e.target.value }))}>
                      <option value="world_major">World Major</option>
                      <option value="gold_label">Gold Label</option>
                      <option value="major">Major</option>
                      <option value="iconic_ultra">Iconic Ultra</option>
                    </select>
                  </label>
                  <label>Month
                    <select value={newCurated.typical_month} onChange={e => setNewCurated((p: any) => ({ ...p, typical_month: Number(e.target.value) }))}>
                      {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                        <option key={i+1} value={i+1}>{m}</option>
                      ))}
                    </select>
                  </label>
                  <label>Terrain
                    <select value={newCurated.terrain} onChange={e => setNewCurated((p: any) => ({ ...p, terrain: e.target.value }))}>
                      <option value="road">Road</option>
                      <option value="trail">Trail</option>
                      <option value="mixed">Mixed</option>
                    </select>
                  </label>
                  <label className="admin-curated-form-wide">Website URL
                    <input value={newCurated.website_url} onChange={e => setNewCurated((p: any) => ({ ...p, website_url: e.target.value }))} placeholder="https://..." />
                  </label>
                  <label className="admin-curated-form-wide">Registration URL
                    <input value={newCurated.registration_url} onChange={e => setNewCurated((p: any) => ({ ...p, registration_url: e.target.value }))} placeholder="https://..." />
                  </label>
                  <label className="admin-curated-form-full">Description
                    <textarea value={newCurated.description} onChange={e => setNewCurated((p: any) => ({ ...p, description: e.target.value }))} rows={2} placeholder="One sentence about the race." />
                  </label>
                </div>
                <div className="admin-curated-form-actions">
                  <button className="btn-admin-sm btn-save" onClick={handleAddCurated}>Add Race</button>
                  <button className="btn-admin-sm btn-cancel" onClick={() => setShowAddCurated(false)}>Cancel</button>
                </div>
              </div>
            )}

            {/* Curated races table */}
            {curatedLoading ? (
              <div className="admin-loading">Loading…</div>
            ) : (
              <div className="admin-table-scroll">
                <table className="admin-table admin-curated-table">
                  <thead>
                    <tr>
                      <th>Race</th>
                      <th>Tier</th>
                      <th>Location</th>
                      <th>Month</th>
                      <th>Terrain</th>
                      <th>Registration URL</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {curatedRaces.map(race => (
                      <React.Fragment key={race.id}>
                        <tr>
                          <td>
                            {editingCurated === race.id ? (
                              <input className="admin-inline-input" value={editingCuratedData.name || ''} onChange={e => setEditingCuratedData((p: any) => ({ ...p, name: e.target.value }))} />
                            ) : (
                              <div>
                                <div className="admin-curated-name">{race.name}</div>
                                {race.description && <div className="admin-curated-desc">{race.description}</div>}
                              </div>
                            )}
                          </td>
                          <td>
                            {editingCurated === race.id ? (
                              <select className="admin-inline-select" value={editingCuratedData.race_tier || ''} onChange={e => setEditingCuratedData((p: any) => ({ ...p, race_tier: e.target.value }))}>
                                <option value="world_major">World Major</option>
                                <option value="gold_label">Gold Label</option>
                                <option value="major">Major</option>
                                <option value="iconic_ultra">Iconic Ultra</option>
                              </select>
                            ) : (
                              <span className={`curated-tier-pill curated-tier-${(race.race_tier || '').replace('_', '-')}`}>
                                {race.race_tier?.replace('_', ' ')}
                              </span>
                            )}
                          </td>
                          <td>
                            {editingCurated === race.id ? (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <input className="admin-inline-input" style={{ width: '80px' }} value={editingCuratedData.city || ''} onChange={e => setEditingCuratedData((p: any) => ({ ...p, city: e.target.value }))} placeholder="City" />
                                <input className="admin-inline-input" style={{ width: '40px' }} value={editingCuratedData.state || ''} onChange={e => setEditingCuratedData((p: any) => ({ ...p, state: e.target.value }))} placeholder="ST" />
                                <input className="admin-inline-input" style={{ width: '40px' }} value={editingCuratedData.country || ''} onChange={e => setEditingCuratedData((p: any) => ({ ...p, country: e.target.value }))} placeholder="US" />
                              </div>
                            ) : (
                              [race.city, race.state, race.country].filter(Boolean).join(', ')
                            )}
                          </td>
                          <td>
                            {editingCurated === race.id ? (
                              <select className="admin-inline-select" value={editingCuratedData.typical_month || 1} onChange={e => setEditingCuratedData((p: any) => ({ ...p, typical_month: Number(e.target.value) }))}>
                                {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                                  <option key={i+1} value={i+1}>{m}</option>
                                ))}
                              </select>
                            ) : (
                              race.typical_month ? new Date(2000, race.typical_month - 1).toLocaleString('en-US', { month: 'short' }) : '—'
                            )}
                          </td>
                          <td>
                            {editingCurated === race.id ? (
                              <select className="admin-inline-select" value={editingCuratedData.terrain || 'road'} onChange={e => setEditingCuratedData((p: any) => ({ ...p, terrain: e.target.value }))}>
                                <option value="road">Road</option>
                                <option value="trail">Trail</option>
                                <option value="mixed">Mixed</option>
                              </select>
                            ) : race.terrain}
                          </td>
                          <td className="admin-curated-url-cell">
                            {editingCurated === race.id ? (
                              <input className="admin-inline-input" value={editingCuratedData.registration_url || ''} onChange={e => setEditingCuratedData((p: any) => ({ ...p, registration_url: e.target.value }))} placeholder="https://..." />
                            ) : race.registration_url ? (
                              <a href={race.registration_url} target="_blank" rel="noopener noreferrer" className="admin-curated-link" onClick={e => e.stopPropagation()}>
                                {new URL(race.registration_url).hostname.replace('www.', '')}
                              </a>
                            ) : <span className="admin-curated-missing">missing</span>}
                          </td>
                          <td>
                            {editingCurated === race.id ? (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button className="btn-admin-sm btn-save" onClick={() => handleSaveCurated(race.id)}>Save</button>
                                <button className="btn-admin-sm btn-cancel" onClick={() => setEditingCurated(null)}>Cancel</button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button className="btn-admin-sm btn-edit" onClick={() => { setEditingCurated(race.id); setEditingCuratedData({ name: race.name, city: race.city, state: race.state, country: race.country, race_tier: race.race_tier, typical_month: race.typical_month, terrain: race.terrain, website_url: race.website_url, registration_url: race.registration_url, description: race.description }); }}>Edit</button>
                                <button className="btn-admin-sm btn-block" onClick={() => handleDeleteCurated(race.id, race.name)}>Delete</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Pending approvals */}
        {activeTab === 'pending' && (
          <div className="admin-section">
            <h2>Pending Approvals ({pendingUsers.length})</h2>
            {pendingUsers.length === 0 ? (
              <p className="admin-empty">No pending users. All caught up!</p>
            ) : (
              <div className="admin-table-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Signed Up</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingUsers.map(u => (
                      <tr key={u.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {u.strava_profile && (
                              <img src={u.strava_profile} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                            )}
                            {u.first_name} {u.last_name}
                          </div>
                        </td>
                        <td>{u.email || '—'}</td>
                        <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              className="admin-btn-approve"
                              disabled={approvingId === u.id}
                              onClick={() => handleApprove(u.id)}
                            >
                              {approvingId === u.id ? 'Approving…' : 'Approve'}
                            </button>
                            <button
                              className="admin-btn admin-btn-danger"
                              onClick={() => handleDeleteUser(u.id, `${u.first_name} ${u.last_name}`)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Users */}
        {activeTab === 'users' && <div className="admin-section">
          <h2>Users ({users.length})</h2>
          <div className="admin-table-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Race Goal</th>
                  <th>Workouts</th>
                  <th>AI msgs/wk</th>
                  <th>Last Login</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <React.Fragment key={u.id}>
                    <tr className={u.is_blocked ? 'admin-row-blocked' : ''}>
                      <td>
                        <div className="admin-user-name">{u.first_name} {u.last_name}</div>
                        <div className="admin-user-email">{u.email || '—'}</div>
                        <div className="admin-user-joined">Joined {fmtDate(u.created_at)}</div>
                      </td>
                      <td>{u.race_name || '—'}</td>
                      <td>{u.total_workouts}</td>
                      <td>{u.coach_messages_this_week}</td>
                      <td>{fmtDate(u.last_login)}</td>
                      <td>
                        <span className={`admin-status-badge ${u.is_blocked ? 'blocked' : u.is_inactive ? 'inactive' : 'active'}`}>
                          {u.is_blocked ? 'Blocked' : u.is_inactive ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {u.is_blocked ? (
                            <button className="btn-admin-sm btn-unblock" onClick={() => handleUnblock(u.id)}>
                              Unblock
                            </button>
                          ) : (
                            <button className="btn-admin-sm btn-block" onClick={() => setBlockingId(u.id)}>
                              Block
                            </button>
                          )}
                          {!u.is_blocked && (
                            <button className="btn-admin-sm btn-delete-plans" onClick={() => handleSetInactive(u.id, !u.is_inactive)}>
                              {u.is_inactive ? 'Reactivate' : 'Mark Inactive'}
                            </button>
                          )}
                          <button className="btn-admin-sm btn-delete-plans" onClick={() => handleDeletePlans(u.id, `${u.first_name} ${u.last_name}`)}>
                            Delete Plans
                          </button>
                          <button className="btn-admin-sm btn-delete-plans" style={{ background: '#ef4444', color: '#fff' }} onClick={() => handleDeleteUser(u.id, `${u.first_name} ${u.last_name}`)}>
                            Delete User
                          </button>
                        </div>
                      </td>
                    </tr>
                    {blockingId === u.id && (
                      <tr className="admin-block-row">
                        <td colSpan={7}>
                          <div className="admin-block-form">
                            <input
                              placeholder="Reason for blocking…"
                              value={blockReason}
                              onChange={e => setBlockReason(e.target.value)}
                            />
                            <button className="btn-admin-sm btn-save" onClick={() => handleBlock(u.id)}>
                              Confirm Block
                            </button>
                            <button className="btn-admin-sm btn-cancel" onClick={() => { setBlockingId(null); setBlockReason(''); }}>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {u.is_blocked && u.blocked_reason && (
                      <tr className="admin-reason-row">
                        <td colSpan={7} className="admin-block-reason">
                          Reason: {u.blocked_reason}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>}

        {/* Email tab */}
        {activeTab === 'email' && (
          <div className="admin-section">
            <h2>Weekly Summary Emails</h2>
            <p style={{ color: '#7A9BB5', marginBottom: '20px', fontSize: '0.9rem' }}>
              Sends a training summary for last week to all approved users with email addresses.
              Scheduled to run automatically every Monday at 8:00 AM UTC.
            </p>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button
                className="btn-admin-sm btn-save"
                disabled={emailSending}
                onClick={async () => {
                  setEmailSending(true);
                  setEmailResult(null);
                  try {
                    const res = await api.adminSendWeeklySummary(false);
                    setEmailResult(res);
                  } catch (e: any) {
                    setEmailResult({ error: e.message });
                  }
                  setEmailSending(false);
                }}
              >
                {emailSending ? 'Sending...' : 'Send Weekly Emails Now'}
              </button>
              <button
                className="btn-admin-sm"
                disabled={emailSending}
                onClick={async () => {
                  setEmailSending(true);
                  setEmailResult(null);
                  try {
                    const res = await api.adminSendWeeklySummary(true);
                    setEmailResult(res);
                  } catch (e: any) {
                    setEmailResult({ error: e.message });
                  }
                  setEmailSending(false);
                }}
              >
                Dry Run (Preview)
              </button>
            </div>
            {emailResult && (
              <div style={{ background: '#172038', borderRadius: '10px', padding: '20px', border: '1px solid rgba(50,162,230,0.15)' }}>
                {emailResult.error ? (
                  <p style={{ color: '#ef4444' }}>Error: {emailResult.error}</p>
                ) : (
                  <>
                    <p style={{ color: '#E8F4F8', fontWeight: 700, marginBottom: '12px' }}>
                      Week: {emailResult.week} — {emailResult.eligible} eligible users
                    </p>
                    <p style={{ color: '#7A9BB5', marginBottom: '16px' }}>
                      Sent: {emailResult.sent} · Skipped: {emailResult.skipped} · Failed: {emailResult.failed}
                    </p>
                    {emailResult.details && emailResult.details.length > 0 && (
                      <table className="admin-table" style={{ fontSize: '0.85rem' }}>
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Email</th>
                            <th>Workouts</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emailResult.details.map((d: any, i: number) => (
                            <tr key={i}>
                              <td>{d.user}</td>
                              <td>{d.email}</td>
                              <td>{d.workouts ?? '—'}</td>
                              <td>
                                <span style={{
                                  color: d.status === 'sent' ? '#22c55e' :
                                         d.status === 'dry_run' ? '#32a2e6' :
                                         '#ef4444',
                                  fontWeight: 600
                                }}>
                                  {d.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
